import React, { useState } from 'react';
import { Panel } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { GroupedBudgetGroup } from '../../types/finance';
import BudgetCategoryRow from './BudgetCategoryRow';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface BudgetGroupPanelProps {
  group: GroupedBudgetGroup;
  defaultExpanded?: boolean;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const BudgetGroupPanel: React.FC<BudgetGroupPanelProps> = ({ group, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isIncome = group.group_type === 'income';
  const hasNonMonthly = group.categories.some(c => c.frequency > 1);

  return (
    <Panel style={{ padding: 0, overflow: 'hidden' }}>
      {/* Group header — clickable to toggle */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 100px 100px 100px',
          padding: `${SPACING[8]} ${SPACING[12]}`,
          backgroundColor: COLORS.bg.hover,
          borderBottom: expanded ? `1px solid ${COLORS.border.default}` : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          {expanded
            ? <ChevronDownIcon style={{ width: '14px', height: '14px', color: COLORS.text.tertiary, flexShrink: 0 }} />
            : <ChevronRightIcon style={{ width: '14px', height: '14px', color: COLORS.text.tertiary, flexShrink: 0 }} />
          }
          <div>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
            }}>
              {group.group_name}
            </div>
            {hasNonMonthly && expanded && (
              <div style={{
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: COLORS.text.tertiary,
                marginTop: '1px',
              }}>
                Monthly set-aside amounts — unused balance rolls over
              </div>
            )}
          </div>
        </div>
        <div style={{
          textAlign: 'right',
          fontSize: TYPOGRAPHY.body.sizes.small,
          fontWeight: '600',
          color: COLORS.text.secondary,
        }}>Budget</div>
        <div style={{
          textAlign: 'right',
          fontSize: TYPOGRAPHY.body.sizes.small,
          fontWeight: '600',
          color: COLORS.text.secondary,
        }}>Actual</div>
        <div style={{
          textAlign: 'right',
          fontSize: TYPOGRAPHY.body.sizes.small,
          fontWeight: '600',
          color: COLORS.text.secondary,
        }}>Remaining</div>
      </div>

      {/* Category rows — only when expanded */}
      {expanded && group.categories.length === 0 && (
        <div style={{
          padding: SPACING[16],
          textAlign: 'center',
          fontSize: TYPOGRAPHY.body.sizes.small,
          color: COLORS.text.tertiary,
        }}>
          No categories configured
        </div>
      )}
      {expanded && group.categories.map((cat) => (
        <BudgetCategoryRow key={cat.budget_id} category={cat} isIncome={isIncome} />
      ))}

      {/* Group totals — always visible */}
      {group.categories.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 100px 100px 100px',
          padding: `${SPACING[8]} ${SPACING[12]}`,
          backgroundColor: COLORS.bg.hover,
          borderTop: expanded ? `1px solid ${COLORS.border.default}` : 'none',
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.default,
          fontWeight: '600',
          color: COLORS.text.primary,
        }}>
          <div>Total</div>
          <div style={{ textAlign: 'right' }}>{formatCurrency(group.total_budgeted)}</div>
          <div style={{ textAlign: 'right' }}>{formatCurrency(group.total_spent)}</div>
          <div style={{
            textAlign: 'right',
            color: group.total_remaining < 0 ? COLORS.status.error : COLORS.text.primary,
          }}>
            {formatCurrency(group.total_remaining)}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default BudgetGroupPanel;
